import React, { CSSProperties } from 'react'
import styled, { StyledProps } from 'styled-components'
import { Label } from '../../form/Label/Label'
import { useId } from '../../../hooks'
import { useTranslation } from 'react-i18next'

const LabelWrapperStyled = styled.div<{
  minWidth?: React.CSSProperties['minWidth']
  maxWidth?: React.CSSProperties['maxWidth']
  width?: React.CSSProperties['width']
}>`
  display: flex;
  flex-direction: column;
  width: ${({ width }) => width};
  min-width: ${({ minWidth }) => minWidth};
  max-width: ${({ maxWidth }) => maxWidth};
`

const WrapperStyled = styled.div<{
  minWidth?: React.CSSProperties['minWidth']
  maxWidth?: React.CSSProperties['maxWidth']
  width?: React.CSSProperties['width']
}>`
  position: relative;
  display: flex;
  align-items: center;
  width: ${({ width }) => width};
  min-width: ${({ minWidth }) => minWidth};
  max-width: ${({ maxWidth }) => maxWidth};
`

const TextAreaWrapperStyled = styled.div<{
  minWidth?: React.CSSProperties['minWidth']
  maxWidth?: React.CSSProperties['maxWidth']
  width?: React.CSSProperties['width']
}>`
  position: relative;
  width: ${({ width }) => width};
  min-width: ${({ minWidth }) => minWidth};
  max-width: ${({ maxWidth }) => maxWidth};
  display: flex;
  align-items: center;
  border-width: 1px;
  border-style: solid;
  border-radius: 4px;
  opacity: 1;
  color: #000;
  border-color: #ddd;
  align-items: flex-start;
  height: 123px;
`

const ErrorStyled = styled.div<StyledProps<{}>>`
  color: ${props => props.theme.color.accentDestructive};
  margin-top: 4px;
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  position: absolute;
  top: 100%;
`

const CounterStyled = styled.span<StyledProps<{}>>`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 20px;
  position: absolute;
  right: 4px;
  bottom: 0;
  color: ${props => props.theme.color.secondary200};
  text-align: right;
`

const Optional = styled.span<StyledProps<{ hasLimit?: boolean }>>`
  font-style: normal;
  font-weight: 500;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.color.accentNotify};
  margin-left: auto;
  text-transform: none;
  position: absolute;
  bottom: calc(100% + 8px);
  right: 0;
`

const Counter: React.FC<{ limit: number; value?: string | number }> = ({ limit, value }) => {
  const total = typeof value === 'string' ? value.length : typeof value === 'number' ? value : 0
  return <CounterStyled>{`${total} / ${limit}`}</CounterStyled>
}

export type FormFieldProps = React.HTMLAttributes<HTMLDivElement> & {
  label?: string
  limit?: number
  value?: string | number
  invalid?: boolean
  errorText?: string
  id?: string
  name?: string
  optional?: boolean
  required?: boolean
  testId?: string
  labelStyle?: CSSProperties
  componentType?: string
  minWidth?: React.CSSProperties['minWidth']
  maxWidth?: React.CSSProperties['maxWidth']
  width?: React.CSSProperties['width']
  render(props: { id?: string; name: string }): React.ReactNode
}

export const FormField: React.FC<FormFieldProps> = ({
  label,
  limit,
  invalid,
  errorText,
  className,
  value,
  name,
  id,
  render,
  optional,
  required,
  testId,
  labelStyle,
  minWidth = '0',
  maxWidth = 'none',
  width = '100%',
  componentType,
  ...props
}) => {
  const { t } = useTranslation('ui')
  const nodeId = useId(id)
  const node = render({ id: nodeId, name: name || '' })
  const Wrapper = componentType && componentType === 'textArea' ? TextAreaWrapperStyled : WrapperStyled
  const field = (
    <Wrapper
      className={className}
      data-test-id={testId}
      minWidth={minWidth}
      maxWidth={maxWidth}
      width={width}
      {...props}
    >
      {node}
      {invalid && <ErrorStyled>{errorText}</ErrorStyled>}
      {typeof limit === 'number' && <Counter value={value} limit={limit} />}
      {optional && <Optional hasLimit={!!limit}>{t('common.optional')}</Optional>}
    </Wrapper>
  )

  if (label) {
    if (required) {
      label = `${label} *`
    }
    return (
      <LabelWrapperStyled minWidth={minWidth} maxWidth={maxWidth} width={width} {...props}>
        <Label htmlFor={name || nodeId} style={labelStyle}>
          {label}
        </Label>
        {field}
      </LabelWrapperStyled>
    )
  }

  return field
}
